import {
  WCAG_AA_TEXT_CONTRAST,
  WCAG_UI_CONTRAST,
  isReadablePair,
  parseColorToRgb,
  resolveAdaptiveForeground,
  resolveForegroundColor,
} from './contrastAwareThemeEngine';
import { applyImageRegionForeground, resolveImageRegionForeground } from './imageRegionContrastEngine';
import { visualAuthority } from './visualAuthority';

const TEXT_SELECTOR = [
  'button',
  '.btn',
  '.MuiButton-root',
  '[role="button"]',
  'a',
  '.nav-link',
  '.dropdown-item',
  '.MuiTab-root',
  '.MuiMenuItem-root',
  '.MuiListItem-root',
  '.MuiListItemButton-root',
  '.MuiAccordionSummary-root',
  '.MuiAccordionDetails-root',
  '.MuiCard-root',
  '.MuiPaper-root',
  '.MuiDialog-paper',
  '.MuiPopover-paper',
  '.MuiMenu-paper',
  '.MuiAlert-root',
  '.MuiChip-root',
  '.MuiTableCell-root',
  '.MuiDataGrid-cell',
  '.MuiDataGrid-columnHeader',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'p',
  'span',
  'strong',
  'small',
  'label',
  'td',
  'th',
  '.MuiTypography-root',
  '.editable-live-text-input',
  'svg:not(.recharts-surface):not([class*="chart" i])',
  '.MuiSvgIcon-root',
  '.MuiIcon-root',
  '.fa',
  '.fas',
  '.far',
  '.fab',
  'i[class*="fa-"]',
  '.og-theme-aware-icon',
  '.og-theme-aware-icon-asset',
].join(',');

const SKIP_SELECTOR = [
  'script',
  'style',
  'noscript',
  'input',
  'textarea',
  'select',
  '.visually-hidden',
  '[data-og-foreground-skip="true"]',
  '[data-og-preserve-colors="true"]',
].join(',');

const INLINE_EDITOR_CONTROL_SELECTOR = '.storefront-inline-editor-shell button';
const SEMANTIC_SURFACE_AUTHORITY_SELECTOR = '[data-og-foreground-authority="semantic-surface"]';
const OPAQUE_SURFACE_SELECTOR = '[data-og-adaptive-surface="true"],[data-og-local-contrast-surface="true"],[data-og-component-surface],.MuiCard-root,.MuiPaper-root';

const TRANSPARENT_BACKGROUND = /rgba?\(\s*0\s*,?\s*0\s*,?\s*0\s*(?:[,/]\s*0(?:\.0+)?)?\s*\)|transparent/i;

const isTransparent = (value = '') => {
  const raw = String(value || '').trim();
  if (!raw || raw === 'none') {
    return true;
  }
  if (raw.toLowerCase() === 'transparent') {
    return true;
  }
  if (TRANSPARENT_BACKGROUND.test(raw)) {
    return true;
  }
  return /rgba?\([^)]*[,/]\s*0(?:\.0+)?\s*\)$/i.test(raw);
};

const cssValue = (element, property) => (
  typeof window === 'undefined' || !element ? '' : window.getComputedStyle(element).getPropertyValue(property).trim()
);

const firstGradientColor = (backgroundImage = '') => {
  const color = String(backgroundImage || '').match(/rgba?\([^)]+\)|#[0-9a-f]{3,8}\b|color\(srgb\s+[^)]+\)/i)?.[0] || '';
  return parseColorToRgb(color) ? color : '';
};

const gradientColors = (backgroundImage = '') => (
  Array.from(String(backgroundImage || '').matchAll(/rgba?\([^)]+\)|#[0-9a-f]{3,8}\b|color\(srgb\s+[^)]+\)/gi))
    .map((match) => match[0])
    .filter((color) => parseColorToRgb(color))
);

const parseCssColor = (value = '') => {
  const raw = String(value || '').trim();
  if (!raw || raw === 'none' || raw.toLowerCase() === 'transparent') {
    return null;
  }
  const rgb = raw.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:[,/]\s*([\d.]+%?))?/i);
  if (rgb) {
    const alphaRaw = rgb[4];
    const alpha = alphaRaw
      ? (String(alphaRaw).endsWith('%') ? Number.parseFloat(alphaRaw) / 100 : Number.parseFloat(alphaRaw))
      : 1;
    return {
      r: Number(rgb[1]),
      g: Number(rgb[2]),
      b: Number(rgb[3]),
      a: Number.isFinite(alpha) ? Math.max(0, Math.min(1, alpha)) : 1,
    };
  }
  const modernRgb = raw.match(/^rgba?\(\s*([\d.]+%?)\s+([\d.]+%?)\s+([\d.]+%?)(?:\s*\/\s*([\d.]+%?))?/i);
  if (modernRgb) {
    const channels = [modernRgb[1], modernRgb[2], modernRgb[3]].map((channel) => (
      String(channel).endsWith('%') ? Math.round((Number.parseFloat(channel) / 100) * 255) : Number.parseFloat(channel)
    ));
    const alphaRaw = modernRgb[4];
    const alpha = alphaRaw
      ? (String(alphaRaw).endsWith('%') ? Number.parseFloat(alphaRaw) / 100 : Number.parseFloat(alphaRaw))
      : 1;
    return { r: channels[0], g: channels[1], b: channels[2], a: Number.isFinite(alpha) ? Math.max(0, Math.min(1, alpha)) : 1 };
  }
  const rgbOnly = parseColorToRgb(raw);
  return rgbOnly ? { r: rgbOnly[0], g: rgbOnly[1], b: rgbOnly[2], a: 1 } : null;
};

const blend = (top, bottom) => {
  if (!top) {
    return bottom;
  }
  if (!bottom) {
    return top;
  }
  const alpha = Math.max(0, Math.min(1, top.a ?? 1));
  return {
    r: Math.round((top.r * alpha) + (bottom.r * (1 - alpha))),
    g: Math.round((top.g * alpha) + (bottom.g * (1 - alpha))),
    b: Math.round((top.b * alpha) + (bottom.b * (1 - alpha))),
    a: 1,
  };
};

const rgbCss = (color) => (color ? `rgb(${Math.round(color.r)}, ${Math.round(color.g)}, ${Math.round(color.b)})` : '');

const averageCssColors = (colors = []) => {
  const parsed = colors.map(parseCssColor).filter(Boolean);
  if (!parsed.length) {
    return null;
  }
  return {
    r: parsed.reduce((sum, color) => sum + color.r, 0) / parsed.length,
    g: parsed.reduce((sum, color) => sum + color.g, 0) / parsed.length,
    b: parsed.reduce((sum, color) => sum + color.b, 0) / parsed.length,
    a: parsed.reduce((sum, color) => sum + color.a, 0) / parsed.length,
  };
};

const inlineBackgroundColor = (element) => {
  if (!element?.style) {
    return '';
  }
  const inlineBackground = element.style.getPropertyValue('background-color')
    || element.style.getPropertyValue('background')
    || '';
  const inlineColor = String(inlineBackground || '').match(/rgba?\([^)]+\)|#[0-9a-f]{3,8}\b|color\(srgb\s+[^)]+\)/i)?.[0] || inlineBackground;
  return inlineColor && !isTransparent(inlineColor) && parseColorToRgb(inlineColor) ? inlineColor : '';
};

const imageToneBackground = (element) => {
  const region = resolveImageRegionForeground(element);
  if (region?.background) {
    return region.background;
  }
  const imageHost = element?.closest?.('[data-og-image-tone]');
  const surface = element?.closest?.(OPAQUE_SURFACE_SELECTOR);
  if (imageHost && surface && surface !== imageHost && imageHost.contains(surface)) {
    return '';
  }
  const tone = imageHost?.dataset?.ogImageTone || '';
  if (!tone) {
    return '';
  }
  if (tone === 'light') {
    return '#f8fafc';
  }
  if (tone === 'dark') {
    return '#0f172a';
  }
  return '#334155';
};

export const getImmediateVisualBackground = (element) => {
  if (!element || typeof window === 'undefined') {
    return '';
  }

  if (!element.matches?.('button,.btn,.MuiButton-root,[role="button"]')) {
    const imageBackground = imageToneBackground(element);
    if (imageBackground) {
      return imageBackground;
    }
  }

  const ownInlineBackground = inlineBackgroundColor(element);
  if (ownInlineBackground) {
    return ownInlineBackground;
  }

  const chain = [];
  let current = element;
  while (current && current.nodeType === 1) {
    chain.unshift(current);
    current = current.parentElement;
  }

  let composited = parseCssColor(cssValue(document.documentElement, 'background-color'))
    || parseCssColor(cssValue(document.body, 'background-color'))
    || { r: 255, g: 255, b: 255, a: 1 };

  for (const node of chain) {
    const backgroundImage = cssValue(node, 'background-image');
    const gradientAverage = averageCssColors(gradientColors(backgroundImage));
    if (gradientAverage) {
      composited = blend(gradientAverage, composited);
      continue;
    }
    const gradientColor = firstGradientColor(backgroundImage);
    if (gradientColor) {
      composited = blend(parseCssColor(gradientColor), composited);
    }
    const backgroundColor = cssValue(node, 'background-color');
    if (backgroundColor && !isTransparent(backgroundColor) && parseColorToRgb(backgroundColor)) {
      composited = blend(parseCssColor(backgroundColor), composited);
    }
  }

  return rgbCss(composited);
};

const shouldUseUiContrast = (element) => (
  element.matches?.('button,.btn,.MuiButton-root,[role="button"],[role="tab"],[role="menuitem"],[role="option"],svg,.MuiSvgIcon-root,.MuiIcon-root,.fa,.fas,.far,.fab,i[class*="fa-"],.og-theme-aware-icon,.og-theme-aware-icon-asset')
);

const hasText = (element) => {
  if (element.matches?.('svg,.MuiSvgIcon-root,.MuiIcon-root,.fa,.fas,.far,.fab,i[class*="fa-"],.og-theme-aware-icon,.og-theme-aware-icon-asset')) {
    return true;
  }
  const text = String(element.textContent || '').trim();
  if (text) {
    return true;
  }
  return Boolean(element.querySelector?.('svg,.MuiSvgIcon-root,.fa,.fas,.far,.fab'));
};

const setStylePropertyIfChanged = (element, property, value) => {
  if (!element || element.style.getPropertyValue(property) === value) {
    return false;
  }
  visualAuthority.requestStyles('ContextAwareForegroundEngine', element, { [property]: value });
  return true;
};

const setImportantStylePropertyIfChanged = (element, property, value) => {
  if (!element || element.style.getPropertyValue(property) === value) {
    return false;
  }
  visualAuthority.requestStyles('ContextAwareForegroundEngine', element, { [property]: value }, 'important');
  return true;
};

const setStyleValueIfChanged = (element, property, value) => {
  if (!element || element.style[property] === value) {
    return false;
  }
  visualAuthority.requestStyles('ContextAwareForegroundEngine', element, { [property]: value });
  return true;
};

const restoreSemanticSurfaceAuthority = (element) => {
  if (element?.dataset?.ogContextForeground !== 'true' && element?.dataset?.ogImageRegionForeground !== 'true') {
    return false;
  }
  [
    '--og-context-foreground',
    '--og-local-foreground',
    '--og-local-icon',
    'color',
    '-webkit-text-fill-color',
    'webkit-text-fill-color',
    'text-shadow',
  ].forEach((property) => visualAuthority.requestStyles('ContextAwareForegroundEngine', element, { [property]: null }));
  delete element.dataset.ogContextForeground;
  delete element.dataset.ogImageRegionForeground;
  return true;
};

const applyExistingLocalSurfaceForeground = (element) => {
  const localSurface = element?.closest?.('[data-og-local-contrast-surface="true"]');
  const foreground = localSurface
    ? window.getComputedStyle(localSurface).getPropertyValue('--og-local-foreground').trim()
    : '';
  if (!foreground || !parseColorToRgb(foreground)) {
    return false;
  }
  setImportantStylePropertyIfChanged(element, '--og-context-foreground', foreground);
  setImportantStylePropertyIfChanged(element, '--og-local-foreground', foreground);
  setImportantStylePropertyIfChanged(element, '--og-local-icon', foreground);
  setImportantStylePropertyIfChanged(element, 'color', foreground);
  setImportantStylePropertyIfChanged(element, '-webkit-text-fill-color', foreground);
  setImportantStylePropertyIfChanged(element, 'webkit-text-fill-color', foreground);
  element.dataset.ogContextForeground = 'true';
  if (element.matches?.('svg,.MuiSvgIcon-root,.MuiIcon-root,.fa,.fas,.far,.fab,i[class*="fa-"],.og-theme-aware-icon,.og-theme-aware-icon-asset')) {
    setStyleValueIfChanged(element, 'fill', 'currentColor');
    setStyleValueIfChanged(element, 'stroke', 'currentColor');
    element.dataset.ogThemeAwareIcon = 'true';
  }
  return true;
};

export const applyContextAwareForeground = (element) => {
  if (element?.closest?.(SEMANTIC_SURFACE_AUTHORITY_SELECTOR)) {
    restoreSemanticSurfaceAuthority(element);
    return false;
  }
  if (
    !element
    || element.matches?.(SKIP_SELECTOR)
    || element.closest?.(SKIP_SELECTOR)
    || element.closest?.(INLINE_EDITOR_CONTROL_SELECTOR)
    || (
      element.closest?.('.og-orders-page, .og-template-orders-page')
      && element.matches?.('.storefront-inline-editable-value[data-og-adaptive-surface="true"], [data-og-inline-editable-value="true"][data-og-adaptive-surface="true"]')
    )
    || !hasText(element)
  ) {
    return false;
  }
  if (!element.matches?.('button,.btn,.MuiButton-root,[role="button"]') && applyImageRegionForeground(element)) {
    return true;
  }
  if (applyExistingLocalSurfaceForeground(element)) {
    return true;
  }
  const background = getImmediateVisualBackground(element);
  if (!background || !parseColorToRgb(background)) {
    return false;
  }
  const computed = window.getComputedStyle(element);
  const currentColor = computed.color;
  const minimumRatio = shouldUseUiContrast(element) ? Math.max(WCAG_UI_CONTRAST, WCAG_AA_TEXT_CONTRAST) : WCAG_AA_TEXT_CONTRAST;
  if (currentColor && isReadablePair(currentColor, background, minimumRatio)) {
    setStylePropertyIfChanged(element, '--og-local-foreground', currentColor);
    setStylePropertyIfChanged(element, '--og-local-icon', currentColor);
    element.querySelectorAll?.('svg,.MuiSvgIcon-root,.MuiIcon-root,.fa,.fas,.far,.fab,i[class*="fa-"],.og-theme-aware-icon,.og-theme-aware-icon-asset').forEach((icon) => {
      setStylePropertyIfChanged(icon, '--og-local-icon', currentColor);
      setStyleValueIfChanged(icon, 'color', 'currentColor');
      setStyleValueIfChanged(icon, 'fill', 'currentColor');
      setStyleValueIfChanged(icon, 'stroke', 'currentColor');
      icon.dataset.ogThemeAwareIcon = 'true';
    });
    if (element.dataset.ogContextForeground === 'true') {
      visualAuthority.requestStyles('ContextAwareForegroundEngine', element, {
        '-webkit-text-fill-color': null,
        'webkit-text-fill-color': null,
        '--og-context-foreground': null,
      });
      delete element.dataset.ogContextForeground;
    }
    return false;
  }
  const nextColor = resolveAdaptiveForeground({
    background,
    preferred: currentColor,
    fallback: resolveForegroundColor(background),
    minimumRatio,
  });
  if (!nextColor || nextColor === currentColor) {
    return false;
  }
  setStylePropertyIfChanged(element, '--og-context-foreground', nextColor);
  setStylePropertyIfChanged(element, '--og-local-foreground', nextColor);
  setStylePropertyIfChanged(element, '--og-local-icon', nextColor);
  setStyleValueIfChanged(element, 'color', nextColor);
  setStylePropertyIfChanged(element, '-webkit-text-fill-color', nextColor);
  setStylePropertyIfChanged(element, 'webkit-text-fill-color', nextColor);
  element.dataset.ogContextForeground = 'true';
  if (element.closest?.('[data-og-image-tone],.og-image-aware,[data-og-image-aware]')) {
    setStyleValueIfChanged(element, 'textShadow', nextColor === '#0f172a'
      ? '0 1px 2px rgba(255,255,255,0.72)'
      : '0 1px 3px rgba(0,0,0,0.62)');
  }
  if (element.matches?.('svg,.MuiSvgIcon-root,.MuiIcon-root,.fa,.fas,.far,.fab,i[class*="fa-"],.og-theme-aware-icon,.og-theme-aware-icon-asset')) {
    setStyleValueIfChanged(element, 'fill', 'currentColor');
    setStyleValueIfChanged(element, 'stroke', 'currentColor');
    element.dataset.ogThemeAwareIcon = 'true';
  }
  element.querySelectorAll?.('svg,.MuiSvgIcon-root,.MuiIcon-root,.fa,.fas,.far,.fab,i[class*="fa-"],.og-theme-aware-icon,.og-theme-aware-icon-asset').forEach((icon) => {
    setStylePropertyIfChanged(icon, '--og-context-foreground', nextColor);
    setStylePropertyIfChanged(icon, '--og-local-icon', nextColor);
    setStyleValueIfChanged(icon, 'color', 'currentColor');
    setStyleValueIfChanged(icon, 'fill', 'currentColor');
    setStyleValueIfChanged(icon, 'stroke', 'currentColor');
    icon.dataset.ogThemeAwareIcon = 'true';
  });
  return true;
};

export const applyContextAwareForegrounds = (root = document) => {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return 0;
  }
  const scope = root?.querySelectorAll ? root : document;
  let updates = 0;
  scope.querySelectorAll(TEXT_SELECTOR).forEach((element) => {
    if (applyContextAwareForeground(element)) {
      updates += 1;
    }
  });
  return updates;
};

let observer = null;
let scheduled = false;

export const scheduleContextAwareForegrounds = () => {
  if (scheduled || typeof window === 'undefined') {
    return;
  }
  scheduled = true;
  window.requestAnimationFrame(() => {
    scheduled = false;
    applyContextAwareForegrounds(document);
  });
};

export const startContextAwareForegroundObserver = () => {
  if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') {
    return () => {};
  }
  if (!observer) {
    observer = new MutationObserver(scheduleContextAwareForegrounds);
    observer.observe(document.body, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ['class', 'style', 'data-og-image-tone', 'data-og-asset-visibility'],
    });
    window.addEventListener('resize', scheduleContextAwareForegrounds);
  }
  scheduleContextAwareForegrounds();
  return () => {
    if (!observer) {
      return;
    }
    observer.disconnect();
    observer = null;
    window.removeEventListener('resize', scheduleContextAwareForegrounds);
  };
};

export const ContextAwareForegroundEngine = {
  getImmediateVisualBackground,
  applyContextAwareForeground,
  applyContextAwareForegrounds,
  scheduleContextAwareForegrounds,
  startContextAwareForegroundObserver,
};
